import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { isIOS } from 'react-device-detect';
import SEOHead from './SEOHead';
import StarryBackground from './StarryBackground';
import { getTripThumbnail } from '../utils/thumbnailGenerator';
import { useI18n } from '../i18n';

const TripPlanPage = ({ primaryAction, secondaryAction, isAppInstalled, isShared = false }) => {
  const { tripId } = useParams();
  const { t } = useI18n();
  const [tripData, setTripData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTripData = async () => {
      try {
        // Simula chiamata API per metadati trip plan (per SEO e preview)
        // In futuro puoi chiamare il tuo backend per ottenere metadati pubblici
        const response = await fetch(`https://your-api.com/trip-metadata/${tripId}`);
        
        if (response.ok) {
          const data = await response.json();
          setTripData(data);
        } else {
          // Fallback: dati generici
          setTripData({
            destination: 'Destinazione sconosciuta',
            duration: 'N/A',
            creator: 'Utente WanderBuddy',
            activities: 0
          });
        }
      } catch (err) {
        console.log('Error fetching trip metadata:', err);
        // Fallback silenzioso
        setTripData({
          destination: 'Itinerario di viaggio',
          duration: 'Più giorni',
          creator: 'Utente WanderBuddy',
          activities: 'Varie attività'
        });
      } finally {
        setLoading(false);
      }
    };

    if (tripId) {
      fetchTripData();
    } else {
      setLoading(false);
    }
  }, [tripId]);

  const pageTitle = isShared 
    ? `${t('trip.shared_title', { destination: tripData?.destination || 'Viaggio' })} | WanderBuddy`
    : `${t('trip.title', { destination: tripData?.destination || 'Viaggio' })} | WanderBuddy`;

  const pageDescription = isShared
    ? t('trip.shared_sub', { destination: tripData?.destination || 'una destinazione fantastica' })
    : t('trip.sub', { destination: tripData?.destination || 'una destinazione fantastica' });

  if (loading) {
    return (
      <>
        <StarryBackground />
        <div className="sora-layout">
          {/* Header capsule */}
          <motion.header 
            className="sora-header"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="sora-header-content">
              <div className="sora-logo-text">
                <img src="/wanderbuddy-white.png" alt="WanderBuddy" style={{ height: '32px', width: 'auto' }} />
              </div>
              <div className="sora-header-actions">
                <motion.a
                  href="https://apps.apple.com/app/id6737308693"
                  className="sora-btn sora-btn-download"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Download
                </motion.a>
                <motion.button
                  className="sora-btn sora-btn-login"
                  disabled
                  style={{ opacity: 0.5, cursor: 'not-allowed' }}
                >
                  Login
                </motion.button>
              </div>
            </div>
          </motion.header>
          <motion.main 
            className="sora-main"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
          >
            <motion.div 
              className="sora-app-icon"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.6 }}
            >
              <div style={{ 
                width: '140px', 
                height: '140px', 
                background: 'rgba(255, 255, 255, 0.1)',
                backdropFilter: 'blur(10px)',
                borderRadius: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '4rem',
                border: '1px solid rgba(255, 255, 255, 0.2)'
              }}>
                🗺️
              </div>
            </motion.div>
            
            <motion.h1 
              className="sora-title"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.6 }}
            >
              {t('landing.desc1')}<br />{t('landing.desc2')}
            </motion.h1>

            <motion.div 
              className="sora-actions"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.6 }}
            >
              <motion.a
                href="https://apps.apple.com/app/id6737308693"
                className="sora-appstore-btn"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <img 
                  src="/app-store-badge.svg" 
                  alt="Download on the App Store" 
                  style={{ height: '48px', width: 'auto' }}
                />
              </motion.a>
              
              {!isIOS && (
                <motion.a
                  href="/waitlist"
                  className="sora-research-btn"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Join the Waitlist
                </motion.a>
              )}
            </motion.div>
          </motion.main>
        </div>
      </>
    );
  }

  return (
    <>
      <SEOHead 
        title={pageTitle}
        description={pageDescription}
        image={getTripThumbnail(tripData, isShared)}
      />

      <StarryBackground />

      <div className="sora-layout">
        {/* Header capsule */}
        <motion.header 
          className="sora-header"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="sora-header-content">
            <div className="sora-logo-text">
              <img src="/wanderbuddy-white.png" alt="WanderBuddy" style={{ height: '32px', width: 'auto' }} />
            </div>
            <div className="sora-header-actions">
              <motion.a
                href="https://apps.apple.com/app/id6737308693"
                className="sora-btn sora-btn-download"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Download
              </motion.a>
              <motion.button
                className="sora-btn sora-btn-login"
                disabled
                style={{ opacity: 0.5, cursor: 'not-allowed' }}
              >
                Login
              </motion.button>
            </div>
          </div>
        </motion.header>

        <motion.div 
          className="sora-container"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        {/* Icon contestuale */}
        <motion.div 
          className="sora-app-icon"
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.6 }}
        >
          <div style={{ 
            width: '140px', 
            height: '140px', 
            background: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(10px)',
            borderRadius: '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '4rem',
            border: '1px solid rgba(255, 255, 255, 0.2)'
          }}>
            {isShared ? '🤝' : '🗺️'}
          </div>
        </motion.div>
        
        {/* Titolo dinamico */}
        <motion.h1 
          className="sora-title"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          {isShared ? t('trip.shared_label') : t('trip.label')}
        </motion.h1>
        
        <motion.p 
          className="sora-description"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          {isShared 
            ? `Someone shared an itinerary for ${tripData?.destination || 'an amazing destination'} with you`
            : `Explore this itinerary for ${tripData?.destination || 'an amazing destination'}`
          }
        </motion.p>

        {/* Trip preview card */}
        {tripData && (
          <motion.div 
            className="sora-info-box"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.5 }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
              <div style={{ 
                width: '60px', 
                height: '60px', 
                background: 'var(--primary-gradient)', 
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '1.5rem'
              }}>
                🏛️
              </div>
              <div style={{ textAlign: 'left', flex: 1 }}>
                <div style={{ fontWeight: '600', fontSize: '1.1rem' }}>
                  {tripData.destination}
                </div>
                <div style={{ color: 'var(--text-light)', fontSize: '0.9rem' }}>
                  {tripData.duration} • {tripData.activities} attività
                </div>
                <div style={{ color: 'var(--text-light)', fontSize: '0.8rem' }}>
                  Creato da {tripData.creator}
                </div>
              </div>
            </div>
            
            <div style={{ fontSize: '0.9rem', lineHeight: '1.5' }}>
              {isShared ? (
                <><strong>🎁 Itinerario condiviso:</strong> Apri nell'app per visualizzare tutti i dettagli e copiarlo nel tuo account.</>
              ) : (
                <><strong>📋 Dettagli completi:</strong> Apri nell'app per vedere tutte le attività, mappe e informazioni dettagliate.</>
              )}
            </div>
          </motion.div>
        )}

        {/* Azioni */}
        <motion.div 
          style={{ margin: '2rem 0' }}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
        >
          {/* Pulsante principale come <a> per supportare Universal Links */}
          <motion.a
            href={primaryAction.href}
            className={`btn ${primaryAction.primary ? 'btn-primary' : 'btn-secondary'} pulse`}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            style={{ 
              pointerEvents: primaryAction.disabled ? 'none' : 'auto',
              opacity: primaryAction.disabled ? 0.6 : 1,
              textDecoration: 'none',
              display: 'inline-block'
            }}
          >
            {primaryAction.disabled && <div className="loading-spinner"></div>}
            {primaryAction.text}
          </motion.a>
          
          {secondaryAction && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.8 }}
            >
              <motion.a
                href={secondaryAction.href}
                className="btn btn-ghost"
                target={secondaryAction.external ? '_blank' : '_self'}
                rel={secondaryAction.external ? 'noopener noreferrer' : ''}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                style={{ textDecoration: 'none', display: 'inline-block' }}
              >
                {secondaryAction.text}
              </motion.a>
            </motion.div>
          )}
        </motion.div>

        {/* Info aggiuntive per trip condivisi */}
        {isShared && (
          <motion.div 
            className="sora-info-box"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
          >
            <strong>How it works:</strong>
            <br />
            1. Open the link in the WanderBuddy app
            <br />
            2. View the complete itinerary  
            <br />
            3. Tap "Copy" to add it to your itineraries
            <br />
            4. Customize and book!
          </motion.div>
        )}

        {/* Link back */}
        <motion.div 
          style={{ marginTop: '2rem' }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.9 }}
        >
          <a 
            href="/"
            className="sora-back-link"
          >
            ← Back to Home
          </a>
        </motion.div>

        {/* Footer */}
        <motion.footer 
          className="sora-footer"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
        >
          WanderBuddy © 2025
        </motion.footer>
        </motion.div>
      </div>
    </>
  );
};

export default TripPlanPage;
